import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigFileModel } from '../model/config-file.model';
import { ConfigServiceModel } from '../model/config-service.model';

/**
 * Constant with the injection token, needed for the dynamic perApp service injection
 */
export const APP_CONFIG_SERVICE = new InjectionToken<any>('app-config.service');

/**
 * AppService load factory
 *
 * @param data
 * @returns
 */
export function loadAppConfigService(appConfigService: ConfigServiceModel): ConfigService {
	const service = new ConfigService(appConfigService);

	return service;
}

@Injectable({
	providedIn: 'root'
})
export class ConfigService {
	get api(): any {
		return this.config.api;
	}
	get version(): any {
		return this.config.version;
	}
	get ids(): any {
		return this.config.oidc_configuration;
	}
	get publicKey(): any {
		return this.config.publicKey;
	}

	private appConfigServiceWrapper: ConfigServiceModel;
	private config: ConfigFileModel = null;

	constructor(@Inject(APP_CONFIG_SERVICE) perAppConfigService: ConfigServiceModel) {
		this.appConfigServiceWrapper = perAppConfigService;
	}

	/**
	 * Loads the API configuration file from the Proxy
	 */
	loadConfigFile(): Observable<ConfigFileModel> {
		return this.appConfigServiceWrapper.loadConfigFile()
			.pipe(
				tap((config: ConfigFileModel) => {
					this.setApiValuesCallback(config);
				})
			);
	}

	/**
	 *
	 * @param config see @ConfigFileModel
	 */
	setApiValuesCallback(config: ConfigFileModel): void {
		this.tempSettings(config);
		localStorage.setItem('config', JSON.stringify(config));
		this.config = config;
	}

	/**
	 * Get the current Config File Model
	 * @param name
	 */
	getConfig(): Observable<ConfigFileModel> {

		if (this.config === null) {
			if (this.appConfigServiceWrapper.config !== null) {
				this.config = this.appConfigServiceWrapper.config;
				this.tempSettings(this.config);
			} else {
				return new Observable((observer: any) => {
					this.loadConfigFile().subscribe((configFile: ConfigFileModel) => {
						this.tempSettings(configFile);
						observer.next(configFile);
						observer.complete();
					});
				});
			}
		}

		return of(this.config);
	}

	/**
	 * get idsConfi from proxy
	 */
	getIdsConfig(): Observable<ConfigFileModel> {
		return this.appConfigServiceWrapper.getIdsConfig();
	}

	/**
	 * get dependencies versions
	 */
	getDependenciesVersions(environment: string): Observable<any> {
		return this.appConfigServiceWrapper.getDependenciesVersions(environment);
	}

	/**
	 * Get release notes
	 *
	 * @returns
	 */
	getReleaseNotes(): Observable<any> {
		return this.appConfigServiceWrapper.getReleaseNotes();
	}

	/**
	 * We need to come back to see how to reuse this across an app with multiple modules
	 */
	public tempSettings(config: ConfigFileModel): void {
		if (config && (!config['secrets'])) {
			config['secrets'] = {
				googleMapsKey: ''
			}

			if (config?.publicKey) {
				config['secrets'] = {
					googleMapsKey: config?.publicKey.googleMaps
				};
			}
		}
	}
}
